import { useEffect } from 'react';
import type { HubConnection } from '@microsoft/signalr';
import { create } from 'zustand';
import { createHub } from './hubConnection';
import { eventBus, type PresenceUser } from './eventBus';
import { hubIdentity } from './hubIdentity';
import { getSessionId } from '@/shared/api/http/tokenProvider';
import { useAuthStore } from '@/entities/session/model/authStore';

type HubEventKind = 'insert' | 'update' | 'delete' | 'refresh';
type HubState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';
interface RealtimeState {
  system: HubState;
  lastError: string | null;
}
export const useRealtimeStore = create<RealtimeState>(() => ({ system: 'disconnected', lastError: null }));

let hub: HubConnection | null = null;
let stopping = false;

function setState(system: HubState, lastError: string | null = null) {
  hubIdentity.set(system === 'connected' ? (hub?.connectionId ?? null) : null);
  useRealtimeStore.setState({ system, lastError });
  eventBus.emit('ConnectionState', system);
}

async function start() {
  if (hub) return;
  stopping = false;
  const h = createHub('/systemhub');
  hub = h;
  h.on('SessionExpired', (byClientInfo?: string) =>
    eventBus.emit('SessionExpired', byClientInfo || undefined),
  );
  h.on('MetadataChanged', (objectId: string) => eventBus.emit('MetadataChanged', objectId));
  h.on('RefreshNavigation', () => eventBus.emit('RefreshNavigation', undefined));
  h.on('RefreshCalendar', () => eventBus.emit('RefreshCalendar', undefined));
  h.on('RefreshGridData', (objectId: string) => eventBus.emit('RefreshGridData', objectId));
  h.on('UpdateData', (sessionId: string, objectId: string, response) =>
    eventBus.emit('UpdateData', { sessionId, objectId, response }),
  );
  h.on('DeleteData', (sessionId: string, objectId: string, keyFieldValue: string) =>
    eventBus.emit('DeleteData', { sessionId, objectId, keyFieldValue }),
  );
  h.on(
    'OpenReportTab',
    (reportActionId: string, caption: string, filterFieldValue: unknown, extra?: string) =>
      eventBus.emit('OpenReportTab', { reportActionId, caption, filterFieldValue, extra }),
  );
  h.on('ScreenPresence', (objectId: string, users: PresenceUser[]) =>
    eventBus.emit('ScreenPresence', { objectId, users: users ?? [] }),
  );
  h.on('RowsChanged', (objectId: string, kind: HubEventKind, keys: string[]) =>
    eventBus.emit('RowsChanged', { objectId, kind, keys: keys ?? [] }),
  );
  // Mesajlaşma ve bildirimler: sunucu yayınlar, olay yoluna olduğu gibi aktarılır.
  for (const name of ['ChatMessage', 'ChatMessageUpdated', 'ChatRead', 'ChatTyping', 'ChatReactions', 'ChatGroupChanged', 'NotificationReceived'] as const)
    h.on(name, (payload: never) => eventBus.emit(name, payload));
  h.on('EditConflict', (objectId: string, rowKey: string, user: PresenceUser) =>
    eventBus.emit('EditConflict', { objectId, rowKey, user }),
  );
  h.on('RowSaved', (objectId: string, rowKey: string, user: PresenceUser) =>
    eventBus.emit('RowSaved', { objectId, rowKey, user }),
  );
  h.onreconnecting(() => setState('reconnecting'));
  h.onreconnected(() => {
    setState('connected');
    void ping();
  });
  h.onclose((err) => {
    setState('disconnected', err?.message ?? null);
    hub = null;
    if (!stopping) setTimeout(() => void start(), 30_000);
  });
  setState('connecting');
  try {
    await h.start();
    setState('connected');
    void ping();
  } catch (err) {
    setState('disconnected', err instanceof Error ? err.message : String(err));
    hub = null;
    if (!stopping) setTimeout(() => void start(), 30_000);
  }
}

async function stop() {
  stopping = true;
  const h = hub;
  hub = null;
  if (h) await h.stop().catch(() => {});
  setState('disconnected');
}

async function ping() {
  const userId = useAuthStore.getState().user?.userId;
  const sessionId = getSessionId();
  if (hub?.state === 'Connected' && userId && sessionId)
    await hub.invoke('PingSession', userId, sessionId).catch(() => {});
}

export const systemHub = {
  start,
  stop,
  ping,
  invoke: (method: string, ...args: unknown[]) =>
    hub?.state === 'Connected' ? hub.invoke(method, ...args) : Promise.resolve(),
  /** Dönüş değeri olan çağrı; bağlı değilken null. */
  call: <T>(method: string, ...args: unknown[]): Promise<T | null> =>
    hub?.state === 'Connected' ? hub.invoke<T>(method, ...args) : Promise.resolve(null),
  /** Bu sekmenin SignalR bağlantı kimliği — varlık listesinde "ben"i ayırmak için. */
  connectionId: () => (hub?.state === 'Connected' ? (hub.connectionId ?? null) : null),
};

/** Kimlik doğrulandığında hub'ı açar, çıkışta kapatır; dakikada bir PingSession. */
export function useSystemHub(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    void start();
    const t = setInterval(() => void ping(), 60_000);
    return () => {
      clearInterval(t);
      void stop();
    };
  }, [enabled]);
}
